<template>
  <div
    class="adjustments-modal-body__grid tw-gap-y-4 tw-gap-x-4 tw-pt-6"
    :style="{ gridTemplateColumns: gridTemplate }"
  >
    <div
      v-for="column in columnsToRender"
      :key="column.name"
      class="tw-uppercase tw-text-xs tw-text-sf-gray tw-font-medium"
      :class="column.classes"
    >
      {{ column.name }}
      <span v-if="column.required" class="sf-c-danger-light-1">
        <abbr title="required">*</abbr>
      </span>
    </div>
    <LoadingRow
      v-if="isLoading"
      :rows="mode === 'new' ? 1 : 3"
      :columns="storedColumns.length"
    />
    <template v-else>
      <Row
        v-for="(subAdjustmentId, index) in subAdjustmentOrder"
        :key="subAdjustmentId"
        v-model="existingSubAdjustments[subAdjustmentId]"
        :focused-column="
          focusedColumn.id === subAdjustmentId ? focusedColumn.column : null
        "
        :focused-at="
          focusedColumn.id === subAdjustmentId ? focusedColumn.timestamp : 0
        "
        :errors="shouldDisplayErrors ? errors[subAdjustmentId] : undefined"
        :has-divider="index !== subAdjustmentOrder.length - 1"
        :index="index"
        :is-description-pristine="
          !editedColumnsPerRow?.[subAdjustmentId]?.description
        "
        :show-actions="
          mode !== 'show' &&
          (index !== subAdjustmentOrder.length - 1 || isLocked)
        "
        @delete="deleteSubAdjustment"
      />
    </template>
    <ControlRow />
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, watch } from 'vue'

import LoadingRow from './adjustments-modal-loading-rows.vue'
import Row from './adjustments-modal-row.vue'
import ControlRow from './adjustments-modal-control-row.vue'
import useModalGridTemplate from '../composables/use-modal-grid-template'

import { useAdjustmentsModalStore } from '@app/application/adjustments/store/adjustment-modal'

interface Props {
  mode: 'new' | 'edit' | 'show' | null
}

withDefaults(defineProps<Props>(), {
  mode: 'new',
})

const modalStore = useAdjustmentsModalStore()

const { addEmptySubAdjustment, deleteSubAdjustment, prefillAdjustment } =
  modalStore

const {
  columns: storedColumns,
  editedColumnsPerRow,
  focusedColumn,
  errors,
  isLoading,
  isLocked,
  shouldDisplayErrors,
  subAdjustmentOrder,
  subAdjustments: existingSubAdjustments,
  emptySubAdjustmentRows,
} = storeToRefs(modalStore)

const columnsToRender = computed(() => {
  return storedColumns.value.map((column, index) => ({
    ...column,
    classes: {
      'tw-pl-6': index === 0,
      'tw-text-right': column.align === 'right',
    },
  }))
})

const { gridTemplate } = useModalGridTemplate()

watch(emptySubAdjustmentRows, (newValue, oldValue) => {
  if (
    Object.keys(oldValue).length === 1 &&
    Object.keys(newValue).length === 0
  ) {
    addEmptySubAdjustment()
    prefillAdjustment(Object.keys(oldValue)[0])
  }
})
</script>

<style lang="scss" scoped>
.adjustments-modal-body {
  &__grid {
    display: grid;
  }
}
</style>
